import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import ProducibleProductAutocomplete from '../shared/ProducibleProductAutocomplete';
import type { ProducibleProduct, ProductionWarehouse } from '../../types/production.types';
import { formatQuantity, isFutureCalendarDate } from '../../utils/productionFormat';

interface ProductionHeaderFormProps {
  disabled?: boolean;
  product: ProducibleProduct | null;
  onProductChange: (product: ProducibleProduct | null) => void;
  plannedQuantity: number | null;
  onPlannedQuantityChange: (value: number | null) => void;
  warehouses: ProductionWarehouse[];
  inputWarehouseId: string;
  onInputWarehouseChange: (id: string) => void;
  outputWarehouseId: string;
  onOutputWarehouseChange: (id: string) => void;
  separateOutput: boolean;
  onSeparateOutputChange: (value: boolean) => void;
  productionDate: Date | null;
  onProductionDateChange: (value: Date | null) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  initialProductId?: string | null;
  onInitialProduct?: (product: ProducibleProduct) => void;
}

function parseQuantity(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

const ProductionHeaderForm: React.FC<ProductionHeaderFormProps> = ({
  disabled,
  product,
  onProductChange,
  plannedQuantity,
  onPlannedQuantityChange,
  warehouses,
  inputWarehouseId,
  onInputWarehouseChange,
  outputWarehouseId,
  onOutputWarehouseChange,
  separateOutput,
  onSeparateOutputChange,
  productionDate,
  onProductionDateChange,
  notes,
  onNotesChange,
  initialProductId,
  onInitialProduct,
}) => {
  const { t, i18n } = useTranslation('production');
  const dateLocale = i18n.language === 'en' ? enLocale : trLocale;
  const future = isFutureCalendarDate(productionDate);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ProducibleProductAutocomplete
            value={product}
            onChange={onProductChange}
            disabled={disabled}
            initialProductId={initialProductId}
            onInitialProduct={onInitialProduct}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            size="small"
            type="number"
            label={t('editor.plannedQuantity')}
            value={plannedQuantity ?? ''}
            disabled={disabled}
            onChange={(event) => onPlannedQuantityChange(parseQuantity(event.target.value))}
            inputProps={{ min: 0, step: 0.001 }}
            InputProps={{
              endAdornment: product?.unit ? <InputAdornment position="end">{product.unit}</InputAdornment> : undefined,
            }}
            helperText={product && product.recipeOutputQuantity > 0
              ? t('editor.recipeHint', {
                quantity: formatQuantity(product.recipeOutputQuantity),
                unit: product.unit,
              })
              : ' '}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required disabled={disabled}>
            <InputLabel>{t('editor.inputWarehouse')}</InputLabel>
            <Select
              label={t('editor.inputWarehouse')}
              value={inputWarehouseId}
              onChange={(event) => onInputWarehouseChange(event.target.value)}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required disabled={disabled || !separateOutput}>
            <InputLabel>{t('editor.outputWarehouse')}</InputLabel>
            <Select
              label={t('editor.outputWarehouse')}
              value={outputWarehouseId}
              onChange={(event) => onOutputWarehouseChange(event.target.value)}
            >
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={separateOutput}
                disabled={disabled}
                onChange={(event) => onSeparateOutputChange(event.target.checked)}
              />
            )}
            label={t('editor.separateOutput')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label={t('editor.productionDate')}
            value={productionDate}
            disabled={disabled}
            onChange={onProductionDateChange}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText: future ? t('editor.futureDate') : ' ',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={t('editor.notes')}
            value={notes}
            disabled={disabled}
            onChange={(event) => onNotesChange(event.target.value.slice(0, 500))}
            inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" color="text.secondary">{notes.length}/500</Typography>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default ProductionHeaderForm;
